import { useLogin } from "@refinedev/core";
import { Button, Card, Checkbox, Form, Input, message, Typography } from "antd";
import { useState } from "react";
import { getAdminAuthCallbackUrl } from "../lib/env";
import { setRememberSession } from "../lib/supabase";
import { tamsThemeColors } from "../theme";

type LoginFormValues = {
	email: string;
	password: string;
	remember: boolean;
};

type ForgotFormValues = {
	email: string;
};

export function LoginPage() {
	const { mutate: login, isPending } = useLogin();
	const [mode, setMode] = useState<"login" | "forgot">("login");
	const [forgotSending, setForgotSending] = useState(false);
	const [loginForm] = Form.useForm<LoginFormValues>();
	const [forgotForm] = Form.useForm<ForgotFormValues>();

	const onLogin = (values: LoginFormValues) => {
		setRememberSession(values.remember);
		login(
			{
				email: values.email.trim(),
				password: values.password,
			},
			{
				onSuccess: (data) => {
					if (!data.success) {
						message.error(data.error?.message ?? "ログインに失敗しました。");
					}
				},
			},
		);
	};

	const onForgot = async (values: ForgotFormValues) => {
		setForgotSending(true);
		try {
			const { getSupabaseClient } = await import("../lib/supabase");
			const { error } = await getSupabaseClient().auth.resetPasswordForEmail(
				values.email.trim(),
				{
					redirectTo: `${getAdminAuthCallbackUrl()}?type=recovery`,
				},
			);
			if (error) {
				message.error(error.message);
				return;
			}
			message.success(
				"パスワード再設定用のメールを送信しました。受信箱を確認してください。",
			);
			setMode("login");
		} catch (err) {
			message.error(
				err instanceof Error
					? err.message
					: "パスワード再設定メールの送信に失敗しました。",
			);
		} finally {
			setForgotSending(false);
		}
	};

	return (
		<div className="admin-login-page">
			<Card className="admin-login-card" bordered={false}>
				<div className="admin-login-brand">たむたむ広場管理画面</div>
				<Typography.Title level={2} className="admin-login-heading">
					{mode === "login" ? "ログイン" : "パスワード再設定"}
				</Typography.Title>

				{mode === "login" ? (
					<Form
						form={loginForm}
						layout="vertical"
						requiredMark={false}
						initialValues={{ remember: true }}
						onFinish={onLogin}
					>
						<Form.Item
							label="ユーザーID"
							name="email"
							rules={[
								{ required: true, message: "ユーザーIDを入力してください" },
								{
									type: "email",
									message: "メールアドレスの形式で入力してください",
								},
							]}
						>
							<Input
								size="large"
								placeholder="ユーザーIDを入力"
								autoComplete="username"
							/>
						</Form.Item>
						<Form.Item
							label="パスワード"
							name="password"
							rules={[
								{ required: true, message: "パスワードを入力してください" },
							]}
						>
							<Input.Password
								size="large"
								placeholder="パスワードを入力"
								autoComplete="current-password"
							/>
						</Form.Item>
						<Form.Item name="remember" valuePropName="checked">
							<Checkbox>ログイン状態を維持する</Checkbox>
						</Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							size="large"
							block
							loading={isPending}
							className="admin-login-submit"
						>
							ログイン
						</Button>
						<button
							type="button"
							className="admin-login-forgot"
							onClick={() => {
								forgotForm.setFieldsValue({
									email: loginForm.getFieldValue("email"),
								});
								setMode("forgot");
							}}
						>
							パスワードをお忘れの方はこちら
						</button>
					</Form>
				) : (
					<Form
						form={forgotForm}
						layout="vertical"
						requiredMark={false}
						onFinish={onForgot}
					>
						<Form.Item
							label="ユーザーID"
							name="email"
							rules={[
								{ required: true, message: "ユーザーIDを入力してください" },
								{
									type: "email",
									message: "メールアドレスの形式で入力してください",
								},
							]}
						>
							<Input
								size="large"
								placeholder="ユーザーIDを入力"
								autoComplete="username"
							/>
						</Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							size="large"
							block
							loading={forgotSending}
							className="admin-login-submit"
						>
							再設定メールを送信
						</Button>
						<button
							type="button"
							className="admin-login-forgot"
							onClick={() => setMode("login")}
							style={{ color: tamsThemeColors.muted }}
						>
							ログインに戻る
						</button>
					</Form>
				)}
			</Card>
		</div>
	);
}
